import { useEffect, useState, useCallback, useRef } from 'react';
import { Select } from '@etherisc/ui-kit';
import { getCountryConfig } from '../../config/countries/index';
import { AdministrativeLevel, FarmerAdministrativeLevels } from '../../config/countries/types';
import { getProvinces, getChildren, getAdministrativeUnitByCode } from '../../services/administrativeUnitService';
import type { AdministrativeUnit } from '../../types/administrativeUnit';

interface ProgressiveAdminUnitSelectorProps {
  countryCode: string;
  onSelectionChange: (selectedUnit: AdministrativeUnit | null, selectionPath?: FarmerAdministrativeLevels) => void;
  disabled?: boolean;
}

interface LocationOption {
  value: string;
  label: string;
}

export function ProgressiveAdminUnitSelector({ 
  countryCode, 
  onSelectionChange, 
  disabled = false 
}: ProgressiveAdminUnitSelectorProps) {
  
  const [levels, setLevels] = useState<AdministrativeLevel[]>([]);
  const [locationOptions, setLocationOptions] = useState<Record<number, LocationOption[]>>({});
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});
  const [values, setValues] = useState<FarmerAdministrativeLevels>({});
  const [selectedUnit, setSelectedUnit] = useState<AdministrativeUnit | null>(null);
  const lastNotifiedUnit = useRef<AdministrativeUnit | null>(null);

  // Load country configuration when country changes
  useEffect(() => {
    const config = getCountryConfig(countryCode);
    if (config) {
      // Filter out country level (level 0) and freetext levels
      const configurableLevels = config.administrativeLevels
        .filter(level => level.level > 0 && level.type === 'predefined')
        .sort((a, b) => a.level - b.level);
      
      setLevels(configurableLevels);
      setLocationOptions({});
      setValues({});
      setSelectedUnit(null);
      lastNotifiedUnit.current = null;
      
      // Notify parent of reset
      onSelectionChange(null, {});
    }
  }, [countryCode]);

  // Load options for a specific level (progressive hierarchy)
  const loadLevelOptions = useCallback(async (level: AdministrativeLevel, parentCode?: string) => {
    setIsLoading(prev => ({ ...prev, [level.level]: true }));
    
    try {
      let units: AdministrativeUnit[] = [];
      
      if (level.level === 1) {
        // Load provinces (top level)
        units = await getProvinces(countryCode);
      } else if (parentCode) {
        // Load children of selected parent
        units = await getChildren(parentCode);
      }
      
      const options: LocationOption[] = units.map((unit: AdministrativeUnit) => ({
        value: unit.adminCode || '', // Use adminCode as value for hierarchy
        label: unit.adminName,
      }));
      
      setLocationOptions(prev => ({ ...prev, [level.level]: options }));
    } catch (error) {
      console.error(`Failed to load options for level ${level.level}:`, error);
      setLocationOptions(prev => ({ ...prev, [level.level]: [] }));
    } finally {
      setIsLoading(prev => ({ ...prev, [level.level]: false }));
    }
  }, [countryCode]);

  // Load initial options for provinces (level 1)
  useEffect(() => {
    if (levels.length > 0) {
      const topLevel = levels[0]; // Should be provinces (level 1)
      loadLevelOptions(topLevel);
    }
  }, [levels, loadLevelOptions]);

  // Handle level value change
  const handleLevelChange = useCallback(async (level: AdministrativeLevel, value: string) => {
    setValues(prevValues => {
      const newValues = { ...prevValues };
      
      // Clear this level and all subsequent levels
      const currentLevelIndex = levels.findIndex(l => l.level === level.level);
      for (let i = currentLevelIndex; i < levels.length; i++) {
        const levelToReset = levels[i];
        if (levelToReset) {
          delete newValues[levelToReset.level.toString()];
          // Clear options for subsequent levels
          if (i > currentLevelIndex) {
            setLocationOptions(prev => ({ ...prev, [levelToReset.level]: [] }));
          }
        }
      }
      
      // Set the new value if provided
      if (value) {
        newValues[level.level.toString()] = value;
        
        // Load options for the next level if it exists (async operation)
        const nextLevelIndex = currentLevelIndex + 1;
        if (nextLevelIndex < levels.length) {
          const nextLevel = levels[nextLevelIndex];
          if (nextLevel) {
            loadLevelOptions(nextLevel, value);
          }
        }
      }
      
      return newValues;
    });
  }, [levels, loadLevelOptions]);


  // Update selected unit when values change
  useEffect(() => {
    const updateUnit = async () => {
      try {
        // Find the most specific (highest level) selection
        let mostSpecificCode: string | null = null;
        let mostSpecificLevel = 0;
        
        for (const [levelStr, code] of Object.entries(values)) {
          const level = parseInt(levelStr);
          if (code && level > mostSpecificLevel) {
            mostSpecificLevel = level;
            mostSpecificCode = code;
          }
        }
        
        if (mostSpecificCode) {
          // Fetch the actual administrative unit from the API
          try {
            const foundUnit = await getAdministrativeUnitByCode(mostSpecificCode);
            
            setSelectedUnit(foundUnit);
            // Only notify if the unit actually changed
            if (lastNotifiedUnit.current?.id !== foundUnit?.id) {
              lastNotifiedUnit.current = foundUnit;
              onSelectionChange(foundUnit, values);
            }
          } catch (error) {
            console.error('Failed to fetch administrative unit:', error);
            setSelectedUnit(null);
            if (lastNotifiedUnit.current !== null) {
              lastNotifiedUnit.current = null;
              onSelectionChange(null, values);
            }
          }
        } else {
          // No selection - default to country level
          const countryUnit: AdministrativeUnit = {
            id: countryCode,
            countryCode,
            adminLevel: 0,
            adminName: countryCode === 'ZW' ? 'Zimbabwe' : countryCode,
            adminCode: countryCode,
            metadata: undefined,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          setSelectedUnit(countryUnit);
          // Only notify if the unit actually changed
          if (lastNotifiedUnit.current?.id !== countryUnit.id) {
            lastNotifiedUnit.current = countryUnit;
            onSelectionChange(countryUnit, values);
          }
        }
      } catch (error) {
        console.error('Error updating selected unit:', error);
        setSelectedUnit(null);
        if (lastNotifiedUnit.current !== null) {
          lastNotifiedUnit.current = null;
          onSelectionChange(null, values);
        }
      }
    };
    
    updateUnit();
  }, [values, locationOptions, countryCode]); // Only depend on values, locationOptions, and countryCode

  const getDisplayName = (unit: AdministrativeUnit | null) => {
    if (!unit) return 'No selection';
    
    if (unit.adminLevel === 0) {
      return unit.adminName; // Just "Zimbabwe"
    } else {
      const levelName = getLevelName(unit.adminLevel);
      return `${levelName} ${unit.adminName}`; // "Province Harare", "District Chitungwiza", etc.
    }
  };

  const getLevelName = (level: number) => {
    switch (level) {
      case 1: return 'Province';
      case 2: return 'District';
      case 3: return 'Ward';
      default: return `Level ${level}`;
    }
  };

  return (
    <div className="space-y-4">
      {/* Country Selection */}
      <div>
        <Select
          label="Country"
          value={countryCode}
          onValueChange={() => {}} // Read-only
          options={[
            { value: countryCode, label: countryCode === 'ZW' ? 'Zimbabwe' : countryCode }
          ]}
          disabled={true}
        />
      </div>

      {/* Progressive Selection */}
      {levels.map((level, index) => {
        const options = locationOptions[level.level] || [];
        const currentValue = values[level.level.toString()] || '';
        const isDisabled = disabled || isLoading[level.level] || (index > 0 && !values[levels[index - 1]?.level.toString()]);
        
        return (
          <div key={level.level}>
            <Select
              label={level.name}
              value={currentValue}
              onValueChange={(value) => handleLevelChange(level, value)}
              options={options}
              disabled={isDisabled}
              placeholder={isLoading[level.level] ? 'Loading...' : `Select ${level.name.toLowerCase()}...`}
            />
          </div>
        );
      })}
      
      {/* Selection Summary */}
      {selectedUnit && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm font-medium text-blue-900">
            Selected Administrative Unit:
          </div>
          <div className="text-blue-800 font-semibold">
            {getDisplayName(selectedUnit)}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Level {selectedUnit.adminLevel} • Code: {selectedUnit.adminCode}
          </div>
        </div>
      )}
    </div>
  );
}
